import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Option } from '../../../../../shared/models/option.model';
import { InputConfig } from '../../../../../shared/ui/form/input/input.component.config';
import { SelectConfig } from '../../../../../shared/ui/form/select/select.component.config';
import { NotificationService } from '../../../../shared/services/notification.service';
import { SubmittedErrorStateMatcher } from '../../../../shared/utils/submitted-error-state.matcher';
import { legacyErrorMessage } from '../../../../shared/utils/legacy-api-response.util';
import { isValidBic, trimmedRequiredValidator } from '../../../../shared/utils/bank-validators.util';
import { BankBicOption } from '../../../bank-account/models/bank-account-form.model';
import { CreditPolicyCrudService } from '../../services/credit-policy-crud.service';
import { CreditPolicyEntity, CreditPolicyFormDialogData, CreditPolicyPayload } from '../../models/credit-policy-form.model';
import { CreditPolicyFormLookupsFacade } from './credit-policy-form-lookups.facade';

type CreditPolicyControl = 'banco' | 'bic' | 'iban' | 'limite';

const BANCO_MAX_LENGTH = 50;
const BIC_MAX_LENGTH = 11;
const IBAN_MAX_LENGTH = 24;

function optionText(value: Option | string | null): string {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return String(value.id || value.display || '');
}

function optionRequiredValidator(control: AbstractControl): ValidationErrors | null {
  return optionText(control.value).trim() ? null : { required: true };
}

function optionBicValidator(control: AbstractControl): ValidationErrors | null {
  const code = optionText(control.value).trim();
  if (!code) return null;
  return isValidBic(code) ? null : { bic: true };
}

function limitRequiredValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  return value === null || value === undefined || value === '' || isNaN(Number(value)) ? { required: true } : null;
}

@Component({
  selector: 'app-credit-policy-form-dialog',
  templateUrl: './credit-policy-form-dialog.component.html',
  styleUrls: ['./credit-policy-form-dialog.component.scss'],
  standalone: false,
  providers: [
    SubmittedErrorStateMatcher,
    { provide: ErrorStateMatcher, useExisting: SubmittedErrorStateMatcher },
  ],
})
export class CreditPolicyFormDialogComponent implements OnInit {
  readonly form = new FormGroup({
    banco: new FormControl<Option | string>('', { nonNullable: true, validators: [optionRequiredValidator, Validators.maxLength(BANCO_MAX_LENGTH)] }),
    bic: new FormControl<Option | string>('', { nonNullable: true, validators: [optionRequiredValidator, optionBicValidator] }),
    iban: new FormControl('', { nonNullable: true, validators: [trimmedRequiredValidator] }),
    limite: new FormControl<number | null>(0, { validators: [limitRequiredValidator] }),
  });

  entity: CreditPolicyEntity | null = null;
  bics: BankBicOption[] = [];
  bicsLoading = false;

  readonly bancoConfig: SelectConfig = {
    label: 'Banco',
    placeholder: 'Busca la entidad…',
    options: [],
    allowCustomValues: true,
    customValueLabel: 'Usar',
    suffixIcon: 'account_balance',
    maxLength: BANCO_MAX_LENGTH,
    maxResults: 50,
    onFocus: () => this.ensureBics(),
    onChange: (value: Option) => this.onBankSelected(value),
    errors: { required: 'Indica el banco' },
  };

  readonly bicConfig: SelectConfig = {
    label: 'BIC / SWIFT',
    placeholder: 'XXXXESMMXXX',
    options: [],
    allowCustomValues: true,
    customValueLabel: 'Usar',
    maxLength: BIC_MAX_LENGTH,
    maxResults: 50,
    onFocus: () => this.ensureBics(),
    errors: { required: 'Indica el BIC/SWIFT', bic: 'Formato BIC/SWIFT incorrecto' },
  };

  readonly limiteConfig: InputConfig = {
    label: 'Límite de crédito',
    type: 'number',
    inputmode: 'decimal',
    step: '0.01',
    suffix: '€',
    errors: { required: 'Indica el límite' },
  };

  readonly ibanConfig: InputConfig = {
    label: 'IBAN',
    placeholder: 'ES00 0000 0000 0000 0000 0000',
    maxLength: IBAN_MAX_LENGTH,
    uppercase: true,
    errors: { required: 'Indica el IBAN' },
  };

  ready = false;
  loadError = '';
  saving = false;
  submitted = false;
  saveError = '';
  showUid = false;

  static open(dialog: MatDialog, data: CreditPolicyFormDialogData): Observable<boolean> {
    return dialog.open<CreditPolicyFormDialogComponent, CreditPolicyFormDialogData, boolean>(CreditPolicyFormDialogComponent, {
      width: '600px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      autoFocus: false,
      data,
    }).afterClosed().pipe(map(result => result === true));
  }

  constructor(
    private dialogRef: MatDialogRef<CreditPolicyFormDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: CreditPolicyFormDialogData,
    private crud: CreditPolicyCrudService,
    private lookups: CreditPolicyFormLookupsFacade,
    private notify: NotificationService,
    private errorState: SubmittedErrorStateMatcher,
  ) { }

  get editUid(): string | null {
    if (this.data && this.data.uid) return this.data.uid;
    return this.data && this.data.policy && this.data.policy.uid ? this.data.policy.uid : null;
  }

  get isEdit(): boolean {
    return !!this.editUid;
  }

  get subtitle(): string {
    if (this.isEdit && this.entity && this.entity.accountingModelsNumber) {
      return [this.entity.banco, 'Cuenta contable ' + this.entity.accountingModelsNumber].filter(Boolean).join(' · ');
    }
    if (this.isEdit) return (this.entity && this.entity.banco) || 'Datos de la póliza';
    return 'Se creará su cuenta contable 5201 automáticamente';
  }

  isInvalid(control: CreditPolicyControl): boolean {
    return this.submitted && this.form.controls[control].invalid;
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loadError = '';
    const uid = this.editUid;
    if (!uid) {
      this.applyEntity(null);
      this.ready = true;
      return;
    }

    const initial = this.data.policy;
    if (initial && initial.uid === uid) {
      this.data.policy = null;
      this.data.uid = uid;
      this.applyEntity(initial);
      this.ready = true;
      return;
    }

    this.ready = false;
    this.crud.get(uid)
      .then(entity => this.applyEntity(entity))
      .catch(error => { this.loadError = legacyErrorMessage(error, 'No se pudo cargar la póliza de crédito.'); })
      .then(() => { this.ready = true; });
  }

  ensureBics(): void {
    if (this.bics.length || this.bicsLoading) return;
    this.bicsLoading = true;
    this.bancoConfig.hint = 'Cargando entidades…';
    this.bicConfig.hint = 'Cargando entidades…';
    this.lookups.bics()
      .then(list => {
        this.bics = list || [];
        this.bancoConfig.options = this.bics.map(bic => ({ id: bic.bank, display: bic.bank, hint: bic.bic }));
        this.bicConfig.options = this.bics.map(bic => ({ id: bic.bic, display: bic.bic, hint: bic.bank }));
      })
      .catch(() => { this.bics = []; })
      .then(() => {
        this.bicsLoading = false;
        this.bancoConfig.hint = '';
        this.bicConfig.hint = '';
      });
  }

  save(): void {
    if (this.saving) return;
    this.submitted = true;
    this.errorState.submitted = true;
    this.saveError = '';
    this.form.markAllAsTouched();
    if (this.form.invalid) return;

    const payload = this.buildPayload();
    const uid = this.isEdit ? payload.uid || this.editUid : null;

    this.saving = true;
    this.dialogRef.disableClose = true;

    const request = uid ? this.crud.update(uid, payload) : this.crud.create(payload);
    request
      .then(() => {
        this.notify.ok(uid ? 'Póliza de crédito actualizada correctamente' : 'Póliza de crédito creada correctamente');
        this.dialogRef.disableClose = false;
        this.dialogRef.close(true);
      })
      .catch(error => {
        this.saving = false;
        this.dialogRef.disableClose = false;
        this.saveError = legacyErrorMessage(error, uid ? 'Ha ocurrido un error inesperado al actualizar la póliza de crédito' : 'Ha ocurrido un error inesperado al añadir la póliza de crédito');
      });
  }

  close(): void {
    if (this.saving) return;
    this.dialogRef.close(false);
  }

  private onBankSelected(value: Option): void {
    const bank = optionText(value).trim();
    if (!bank) return;
    const match = this.bics.find(b => b.bank === bank);
    if (!match) return;
    this.form.controls.bic.setValue({ id: match.bic, display: match.bic, hint: match.bank });
  }

  private applyEntity(source: CreditPolicyEntity | null): void {
    this.entity = source ? {
      uid: source.uid,
      accountingModelsNumber: source.accountingModelsNumber || '',
      banco: source.banco || '',
      bic: source.bic || '',
      iban: source.iban || '',
      limite: Number(source.limite) || 0,
      projectUid: source.projectUid,
      expenseProjectTypeUid: source.expenseProjectTypeUid,
    } : null;

    const banco = this.entity ? this.entity.banco : '';
    const bic = this.entity ? this.entity.bic : '';
    this.form.reset({
      banco: banco ? { id: banco, display: banco } : '',
      bic: bic ? { id: bic, display: bic } : '',
      iban: this.entity ? this.entity.iban.toUpperCase() : '',
      limite: this.entity ? this.entity.limite : 0,
    });
  }

  private buildPayload(): CreditPolicyPayload {
    const value = this.form.getRawValue();
    const base = this.entity;
    const payload: CreditPolicyPayload = {
      uid: base ? base.uid : '',
      accountingModelsNumber: base ? base.accountingModelsNumber : '',
      banco: optionText(value.banco).trim().substring(0, BANCO_MAX_LENGTH),
      bic: optionText(value.bic).trim().toUpperCase().substring(0, BIC_MAX_LENGTH),
      iban: String(value.iban || '').trim().toUpperCase().substring(0, IBAN_MAX_LENGTH),
      limite: Number(value.limite) || 0,
    };
    if (!base) payload.connected = false;
    if (base && base.projectUid) payload.projectUid = base.projectUid;
    if (base && base.expenseProjectTypeUid) payload.expenseProjectTypeUid = base.expenseProjectTypeUid;
    return payload;
  }
}
